import { useState, useCallback, useMemo, memo, useRef, useEffect } from "react";
import { MessageCircle, Cpu, Book, ExternalLink, BarChart3, ClipboardCheck, FileText, X } from "lucide-react";
import "./aiAssistant.scss";
import ChatTab from "./tab/ChatTab";
import GuideTab from "./tab/GuideTab";
import SuggestTab from "./tab/SuggestTab";
import { useAiAssistant } from "../../../_theme/context/AiAssistantContext";

type TabType = "chat" | "assistant";
type Message = { sender: 'ai' | 'user'; text: string; id: string };

// Constants extracted outside component
const TABS = [
  { id: "assistant" as const, label: "Assistant", icon: Cpu },
  { id: "chat" as const, label: "Chat", icon: MessageCircle },
] as const;

const QUICK_ACTIONS = [
  "Validate SG4 readiness",
  "Sensitivity analysis",
  "Compare CBA vs CEA",
  "Check IFISA compliance",
  "Simulate approval",
] as const;

const POLICY_REFERENCES = [
  "NT Infrastructure Planning Framework (BFI 2026)",
  "Infrastructure Fiscal Sustainability Assessment (IFISA)",
  "Cost-Benefit Analysis (CBA)",
  "Cost-Effectiveness Analysis (CEA)",
  "ISO 9001 Quality Management System",
  "Western Cape Infrastructure Strategy (WC PSP)",
] as const;

const MENU_ITEMS = [
  { label: "Investment Dashboard", Icon: BarChart3 },
  { label: "Audit Trail", Icon: ClipboardCheck },
  { label: "Document Uploads", Icon: FileText },
] as const;

const AI_RESPONSES = {
  readiness: `SG4 readiness: PASS ✅

Investment Readiness: 82%
Feasibility: 85%
Fiscal: 78%
Risk: 9/25

Recommendation: Approve for Investment.`,

  sensitivity: `Cost sensitivity analysis:

Base BCR: 1.65
+10% cost: BCR 1.50
+20% cost: BCR 1.38
-10% benefit: BCR 1.49

BCR remains above 1.0 under all scenarios.`,

  cbaCea: `CBA vs CEA analysis:

Mode selected: CBA

• CBA (Cost-Benefit Analysis): Activated when ≥60% of project impacts are monetisable. Produces BCR and NPV.
• CEA (Cost-Effectiveness Analysis): Applied when outcomes are non-monetisable. Produces cost-per-outcome-unit.

NT BFI 2026 logic: CBA selected — confirm all benefits are shadow-priced.`,

  ifsa: `IFISA fiscal compliance check:

Affordability index: 72%
Contingent liability: Low
Fiscal score: 78%

IFISA compliance: PASS

Required: Affordability index ≥60%, contingent liability ≤Medium.`,

  approval: `Approval scenario simulation:

Current Investment Readiness: 82%
Risk Score: 9/25

Simulated outcome: APPROVE FOR INVESTMENT

To improve:
• Reduce residual implementation risk
• Strengthen fiscal monitoring controls`,

  default: `SG4 Investment Intelligence Engine ready.

Investment Readiness: 82%
Feasibility: 85%
Fiscal: 78%
Risk: 9/25

Try:
• Validate SG4 readiness
• Run sensitivity analysis
• Compare CBA vs CEA outcomes
• Check IFISA compliance risk
• Simulate approval scenarios`
};

// Optimized AI reply function with Map for O(1) lookups
const AI_RESPONSE_MAP = new Map([
  [/(readiness|validate|sg4)/i, AI_RESPONSES.readiness],
  [/(sensitivity|cost)/i, AI_RESPONSES.sensitivity],
  [/(cba|cea|compare)/i, AI_RESPONSES.cbaCea],
  [/(ifsa|fiscal)/i, AI_RESPONSES.ifsa],
  [/(scenario|approval|simulate)/i, AI_RESPONSES.approval],
]);

function getAiReply(question: string): string {
  const q = question.toLowerCase();
  for (const [pattern, response] of AI_RESPONSE_MAP) {
    if (pattern.test(q)) {
      return response;
    }
  }
  return AI_RESPONSES.default;
}

// Custom hook for message management
const useMessages = (initialMessage: string, setLoading: (loading: boolean) => void, loading: boolean) => {
  const [messages, setMessages] = useState<Message[]>([
    { sender: 'ai', text: initialMessage, id: crypto.randomUUID() },
  ]);
  const [input, setInput] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sendMessage = useCallback((text?: string) => {
    const question = text ?? input;
    if (!question.trim()) return;

    setInput('');
    setMessages(prev => [...prev, { sender: 'user', text: question, id: crypto.randomUUID() }]);
    setLoading(true);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setMessages(prev => [...prev, { sender: 'ai', text: getAiReply(question), id: crypto.randomUUID() }]);
      setLoading(false);
    }, 500);
  }, [input]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const resetMessages = useCallback(() => {
    setMessages([{ sender: 'ai', text: initialMessage, id: crypto.randomUUID() }]);
    setLoading(false);
  }, [initialMessage]);

  return { messages, input, setInput, sendMessage, resetMessages, loading };
};

const AiAssistant = () => {
  const [Loading, setLoading] = useState(false);
  const { isOpen, closeAssistant, stageId, subStageId, fieldName } = useAiAssistant();
  const [activeTab, setActiveTab] = useState<TabType>("assistant");

  // Use custom hook for message management
  const { messages, input, setInput, sendMessage, resetMessages, loading } = useMessages(
    'SG4 Investment Intelligence Engine active. I can validate readiness, run cost sensitivity, compare CBA vs CEA, check IFISA compliance, or simulate approval scenarios.', setLoading, Loading
  );

  // When an AI response follows a user message, switch to the chat tab
  useEffect(() => {
    if (messages.length < 2) return;
    const last = messages[messages.length - 1];
    const prev = messages[messages.length - 2];
    if (last.sender === 'ai' && prev && prev.sender === 'user') {
      setActiveTab("chat");
    }
  }, [messages]);

  // Memoized handlers
  const handleTabChange = useCallback((tab: TabType) => {
    setActiveTab(tab);
  }, []);

  const handleQuickAction = useCallback((action: string) => {
    sendMessage(action);
  }, [sendMessage]);

  const handleReferenceClick = useCallback((reference: string) => {
    // Use console.log sparingly in production - consider using a proper logging service

    console.log(`Reference clicked: ${reference}`);

  }, []);

  const handleMenuClick = useCallback((menuItem: string) => {

    console.log(`Menu item clicked: ${menuItem}`);

  }, []);

  // Memoized tab content with proper dependencies
  const tabContent = useMemo(() => {
    if (activeTab === "chat") {
      return (
        <ChatTab
          stage_id={stageId}
          sub_stage_id={subStageId}
          msgs={messages}
        />
      );
    }

    return (
      <div className="assistant-content">
        <QuickActionsSection
          actions={QUICK_ACTIONS}
          onActionClick={handleQuickAction}
          loading={loading}
        />
        <PolicyReferencesSection
          references={POLICY_REFERENCES}
          onReferenceClick={handleReferenceClick}
        />
      </div>
    );
  }, [activeTab, messages, stageId, subStageId, handleQuickAction, handleReferenceClick]);

  const handleClose = useCallback(() => {
    closeAssistant();
    resetMessages();
    setActiveTab("assistant");
  }, [closeAssistant, resetMessages]);

  return (
    <div className="w-100 col-12 col-md-3">
      <div className="card">
        <Header onClose={handleClose} />
        <div className="card-body p-4" style={{ overflowY: "auto" }}>
          <MenuSection items={MENU_ITEMS} onItemClick={handleMenuClick} />

          <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />

          {loading && (
            <div className="d-flex align-items-center gap-2 mt-3 mb-2">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="text-muted">Thinking...</div>
            </div>
          )}

          <div className="mt-3">{tabContent}</div>
        </div>

      </div>
    </div>
  );
};

// Optimized sub-components with proper memoization
const Header = memo(({ onClose }: { onClose: () => void }) => (
  <div className="card-header d-flex justify-content-between align-items-center bg-ai-header">
    <div className="fw-bolder text-white fs-3">
      <MessageCircle className="text-white me-2" size={20} />
      AI Assistant
    </div>
    <button
      className="btn btn-sm btn-icon text-white"
      onClick={onClose}
      aria-label="Close AI Assistant"
    >
      <X size={20} />
    </button>
  </div>
));

Header.displayName = "Header";

const QuickActionsSection = memo(({
  actions,
  onActionClick,
  loading
}: {
  actions: readonly string[];
  onActionClick: (action: string) => void;
  loading: boolean;
}) => (
  <div className="mt-4">
    <div className="fw-bold border-bottom border-top py-2">
      <Cpu className="text-primary me-2" size={20} />
      Quick Actions
    </div>
    <div className="px-0 py-2 d-flex flex-wrap gap-2">
      {actions.map((action) => (
        <button
          key={action}
          className="btn btn-sm border border-gray-300 btn-outline-secondary px-3 py-2 rounded-pill"
          onClick={() => onActionClick(action)}
          disabled={loading}
        >
          {action}
        </button>
      ))}
    </div>
  </div>
));

QuickActionsSection.displayName = "QuickActionsSection";

const PolicyReferencesSection = memo(({
  references,
  onReferenceClick
}: {
  references: readonly string[];
  onReferenceClick: (reference: string) => void;
}) => (
  <div className="mt-4">
    <div className="fw-bold border-bottom border-top py-2">
      <Book className="text-primary me-2" size={20} />
      Policy & Framework References
    </div>
    <div className="px-0 py-2 d-flex flex-wrap gap-2">
      {references.map((reference) => (
        <a
          key={reference}
          href="#"
          className="reference-link d-flex align-items-start gap-2 px-2 py-2 rounded text-decoration-none"
          onClick={(e) => {
            e.preventDefault();
            onReferenceClick(reference);
          }}
        >
          <ExternalLink size={12} className="reference-icon flex-shrink-0 mt-0" />
          <span>{reference}</span>
        </a>
      ))}
    </div>
  </div>
));

PolicyReferencesSection.displayName = "PolicyReferencesSection";

const TabNavigation = memo(({
  activeTab,
  onTabChange
}: {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}) => (
  <div className="border-bottom mt-3">
    <div className="d-flex gap-2">
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            className={`btn btn-sm px-3 py-2 d-flex align-items-center gap-2 ${isActive
              ? "text-primary border-bottom border-2 border-primary rounded-0 fw-bold"
              : "text-gray-400 border-0"
              }`}
            onClick={() => onTabChange(tab.id)}
            aria-selected={isActive}
            role="tab"
          >
            <Icon size={16} />
            {tab.label}
          </button>
        );
      })}
    </div>
  </div>
));

TabNavigation.displayName = "TabNavigation";

const MenuSection = memo(({
  items,
  onItemClick
}: {
  items: typeof MENU_ITEMS;
  onItemClick: (label: string) => void;
}) => (
  <div className="flex-shrink-0">
    {items.map(({ label, Icon }) => (
      <button
        key={label}
        type="button"
        className="btn px-0 py-1 w-100 d-flex align-items-center gap-2 text-start text-dark mb-1"
        onClick={() => onItemClick(label)}
      >
        <Icon size={14} className="flex-shrink-0" />
        <span>{label}</span>
      </button>
    ))}
  </div>
));

MenuSection.displayName = "MenuSection";

export default AiAssistant;